import React, { useState, useEffect, createContext, useContext } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import api from '../utils/api';
import { PageTitle, Button, KPI, KPIStrip, Icons } from '../shared/ui';
import { VND, pct } from '../shared/utils/format';

const {
  Plus: PlusIcon, X: XMarkIcon, Cog: Cog6ToothIcon, Check: CheckIcon, Chart: ChartBarIcon,
  Users: UsersIcon, Home: HomeIcon, Alert: ExclamationTriangleIcon, DollarSign: CurrencyDollarIcon,
  Document: DocumentTextIcon, Sparkles: SparklesIcon, TrendingUp: ArrowTrendingUpIcon,
  Edit: PencilIcon, Trash: TrashIcon, GripVertical: ArrowsPointingOutIcon,
  Calendar: CalendarDaysIcon, ChevronDown: ChevronDownIcon, Ticket: TicketIcon
} = Icons;
import {
  OccupancyWidget,
  RevenueChartWidget,
  RecentTicketsWidget,
  MaintenanceAlertsWidget,
  TenantStatsWidget,
  PaymentTrendsWidget,
  BuildingOverviewWidget,
  ActivityFeedWidget,
  RoomIncomeWidget,
  BuildingFinancialWidget,
  ElectricUsageWidget,
  WaterUsageWidget,
  BuildingUsageComparisonWidget,
  MeterPredictionWidget,
  AdjustmentsWidget,
  ExpenseSummaryWidget,
  IncomeVsExpenseWidget,
  ContractExpiryWidget,
  WidgetSettingsModal
} from '../components/dashboard/DashboardWidgets';

// Time Range Context for dashboard-wide time filtering
export const DashboardTimeRangeContext = createContext();
export const useDashboardTimeRange = () => useContext(DashboardTimeRangeContext);

// Time range presets
const TIME_RANGE_PRESETS = [
  { id: '1m', label: '1 Month', months: 1 },
  { id: '3m', label: '3 Months', months: 3 },
  { id: '6m', label: '6 Months', months: 6 },
  { id: '12m', label: '12 Months', months: 12 },
  { id: 'ytd', label: 'Year to Date', months: 'ytd' },
  { id: 'custom', label: 'Custom', months: 'custom' }
];

// Helper to calculate date range
const getDateRange = (preset, customRange = null) => {
  const now = new Date();
  const endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0); // End of current month
  let startDate;

  if (preset === 'custom' && customRange) {
    return customRange;
  }

  if (preset === 'ytd') {
    startDate = new Date(now.getFullYear(), 0, 1); // Jan 1st of current year
  } else {
    const months = TIME_RANGE_PRESETS.find(p => p.id === preset)?.months || 6;
    startDate = new Date(now.getFullYear(), now.getMonth() - months + 1, 1);
  }

  return {
    startDate: startDate.toISOString().slice(0, 10),
    endDate: endDate.toISOString().slice(0, 10),
    startMonth: startDate.toISOString().slice(0, 7),
    endMonth: endDate.toISOString().slice(0, 7),
    preset
  };
};

// Widget type definitions
const WIDGET_TYPES = {
  STATS: 'stats',
  CHART: 'chart',
  LIST: 'list',
  ALERT: 'alert',
  CUSTOM: 'custom'
};

// Available widget templates
const WIDGET_TEMPLATES = [
  { id: 'occupancy-rate', name: 'Occupancy Rate', type: WIDGET_TYPES.STATS, icon: HomeIcon },
  { id: 'revenue-chart', name: 'Revenue Chart', type: WIDGET_TYPES.CHART, icon: CurrencyDollarIcon },
  { id: 'recent-tickets', name: 'Recent Tickets', type: WIDGET_TYPES.LIST, icon: DocumentTextIcon },
  { id: 'maintenance-alerts', name: 'Maintenance Alerts', type: WIDGET_TYPES.ALERT, icon: ExclamationTriangleIcon },
  { id: 'tenant-stats', name: 'Tenant Statistics', type: WIDGET_TYPES.STATS, icon: UsersIcon },
  { id: 'payment-trends', name: 'Payment Trends', type: WIDGET_TYPES.CHART, icon: ArrowTrendingUpIcon },
  { id: 'building-overview', name: 'Building Overview', type: WIDGET_TYPES.LIST, icon: HomeIcon },
  { id: 'activity-feed', name: 'Activity Feed', type: WIDGET_TYPES.LIST, icon: SparklesIcon },
  { id: 'room-income', name: 'Room Income', type: WIDGET_TYPES.CHART, icon: CurrencyDollarIcon },
  { id: 'building-financial', name: 'Building Financial', type: WIDGET_TYPES.CHART, icon: ChartBarIcon },
  { id: 'electric-usage', name: 'Electric Usage', type: WIDGET_TYPES.CHART, icon: ChartBarIcon },
  { id: 'water-usage', name: 'Water Usage', type: WIDGET_TYPES.CHART, icon: ChartBarIcon },
  { id: 'building-usage-comparison', name: 'Building Usage Comparison', type: WIDGET_TYPES.CHART, icon: ChartBarIcon },
  { id: 'meter-prediction', name: 'Meter Prediction', type: WIDGET_TYPES.CHART, icon: ArrowTrendingUpIcon },
  { id: 'adjustments', name: 'Service Fee Adjustments', type: WIDGET_TYPES.STATS, icon: CurrencyDollarIcon },
  { id: 'expense-summary', name: 'Expense Summary', type: WIDGET_TYPES.STATS, icon: CurrencyDollarIcon },
  { id: 'income-vs-expense', name: 'Service Income vs Expense', type: WIDGET_TYPES.CHART, icon: ChartBarIcon },
  { id: 'contract-expiry', name: 'Contract Expiry Tracker', type: WIDGET_TYPES.LIST, icon: CalendarDaysIcon }
];

const DashboardEnhanced = () => {
  const queryClient = useQueryClient();
  const { t } = useTranslation(['dashboard', 'common']);
  const [widgets, setWidgets] = useState([]);
  const [isEditMode, setIsEditMode] = useState(false);
  const [showAddWidget, setShowAddWidget] = useState(false);
  const [editingWidget, setEditingWidget] = useState(null);

  // Global time range state
  const [timeRangePreset, setTimeRangePreset] = useState('6m');
  const [customRange, setCustomRange] = useState(null);
  const [showTimeRangeDropdown, setShowTimeRangeDropdown] = useState(false);

  // Calculate actual date range based on preset
  const timeRange = getDateRange(timeRangePreset, customRange);

  // Fetch user's saved widget configuration
  const { data: savedWidgets, isLoading: widgetsLoading } = useQuery({
    queryKey: ['dashboard-widgets'],
    queryFn: async () => {
      const response = await api.get('/api/dashboard/widgets');
      return response.data;
    }
  });

  // Fetch dashboard statistics
  const { data: stats } = useQuery({
    queryKey: ['dashboard-stats'],
    queryFn: async () => {
      const response = await api.get('/api/stats/dashboard');
      return response.data;
    }
  });

  // Save widget configuration mutation
  const saveWidgetsMutation = useMutation({
    mutationFn: async (widgetConfig) => {
      return api.post('/api/dashboard/widgets', { widgets: widgetConfig });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dashboard-widgets'] });
      toast.success(t('dashboard:toast.configSaved'));
    },
    onError: (err) => {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail || err?.message || t('dashboard:toast.saveFailedDefault');
      console.error('saveWidgetsMutation failed:', status, detail, err);
      toast.error(status === 401 ? t('dashboard:toast.saveFailedExpired') : t('dashboard:toast.saveFailed', { detail }));
    }
  });

  useEffect(() => {
    // Wait for the query to resolve before deciding — undefined means "still loading"
    if (savedWidgets === undefined) return;

    // Backend returns an array of widget configs. A non-empty array is the
    // user's saved layout; anything else (empty array, null, unexpected shape)
    // falls back to defaults so the dashboard is never blank.
    if (Array.isArray(savedWidgets) && savedWidgets.length > 0) {
      setWidgets(savedWidgets);
      return;
    }

    if (savedWidgets && !Array.isArray(savedWidgets)) {
      console.warn('Widgets data is not an array:', savedWidgets);
    }

    setWidgets([
      { id: '1', type: 'occupancy-rate', position: 0, size: 'small' },
      { id: '2', type: 'revenue-chart', position: 1, size: 'medium' },
      { id: '3', type: 'recent-tickets', position: 2, size: 'medium' },
      { id: '4', type: 'maintenance-alerts', position: 3, size: 'small' }
    ]);
  }, [savedWidgets]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showTimeRangeDropdown && !event.target.closest('[data-time-range-dropdown]')) {
        setShowTimeRangeDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showTimeRangeDropdown]);

  // Handle drag and drop
  const handleDragEnd = (result) => {
    if (!result.destination) return;

    const items = Array.from(widgets);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    // Update positions
    const updatedItems = items.map((item, index) => ({
      ...item,
      position: index
    }));

    setWidgets(updatedItems);
  };

  // Add new widget
  const addWidget = (template) => {
    const newWidget = {
      id: `widget-${Date.now()}`,
      type: template.id,
      name: template.name,
      position: widgets.length,
      size: 'medium',
      settings: {}
    };
    setWidgets([...widgets, newWidget]);
    setShowAddWidget(false);
  };

  // Remove widget
  const removeWidget = (widgetId) => {
    setWidgets(widgets.filter(w => w.id !== widgetId));
  };

  // Update widget settings
  const updateWidget = (widgetId, updates) => {
    setWidgets(widgets.map(w =>
      w.id === widgetId ? { ...w, ...updates } : w
    ));
    setEditingWidget(null);
  };

  // Save widget configuration
  const saveConfiguration = () => {
    saveWidgetsMutation.mutate(widgets);
    setIsEditMode(false);
  };

  // Render individual widget based on type - passing timeRange to all
  const renderWidget = (widget) => {
    const template = WIDGET_TEMPLATES.find(t => t.id === widget.type);
    if (!template) return null;

    switch (widget.type) {
      case 'occupancy-rate':
        return <OccupancyWidget timeRange={timeRange} />;
      case 'revenue-chart':
        return <RevenueChartWidget timeRange={timeRange} />;
      case 'recent-tickets':
        return <RecentTicketsWidget timeRange={timeRange} />;
      case 'maintenance-alerts':
        return <MaintenanceAlertsWidget timeRange={timeRange} />;
      case 'tenant-stats':
        return <TenantStatsWidget timeRange={timeRange} />;
      case 'payment-trends':
        return <PaymentTrendsWidget timeRange={timeRange} />;
      case 'building-overview':
        return <BuildingOverviewWidget timeRange={timeRange} />;
      case 'activity-feed':
        return <ActivityFeedWidget timeRange={timeRange} />;
      case 'room-income':
        return <RoomIncomeWidget timeRange={timeRange} />;
      case 'building-financial':
        return <BuildingFinancialWidget timeRange={timeRange} />;
      case 'electric-usage':
        return <ElectricUsageWidget timeRange={timeRange} />;
      case 'water-usage':
        return <WaterUsageWidget timeRange={timeRange} />;
      case 'building-usage-comparison':
        return <BuildingUsageComparisonWidget timeRange={timeRange} />;
      case 'meter-prediction':
        return <MeterPredictionWidget timeRange={timeRange} />;
      case 'adjustments':
        return <AdjustmentsWidget timeRange={timeRange} />;
      case 'expense-summary':
        return <ExpenseSummaryWidget timeRange={timeRange} />;
      case 'income-vs-expense':
        return <IncomeVsExpenseWidget timeRange={timeRange} />;
      case 'contract-expiry':
        return <ContractExpiryWidget />;
      default:
        return <div className="text-stone-500 text-center">Widget not found</div>;
    }
  };

  // Widget size classes
  const getWidgetSizeClass = (size) => {
    switch (size) {
      case 'small': return 'col-span-1';
      case 'medium': return 'col-span-2';
      case 'large': return 'col-span-3';
      case 'full': return 'col-span-4';
      default: return 'col-span-2';
    }
  };

  return (
    <DashboardTimeRangeContext.Provider value={{ timeRange, setTimeRangePreset, setCustomRange }}>
    <div className="space-y-6 animate-fade-in">
      {/* Page header */}
      <PageTitle
        title={t('dashboard:title')}
        subtitle={t('dashboard:subtitle')}
        actions={
          isEditMode ? (
            <>
              <Button kind="secondary" size="sm" iconLeft={<Icons.Plus size={14} />} onClick={() => setShowAddWidget(true)}>
                {t('dashboard:action.addWidget')}
              </Button>
              <Button kind="primary" size="sm" iconLeft={<Icons.Check size={14} />} onClick={saveConfiguration}>
                {t('dashboard:action.saveLayout')}
              </Button>
              <Button kind="ghost" size="sm" onClick={() => setIsEditMode(false)}>{t('common:action.cancel')}</Button>
            </>
          ) : (
            <Button kind="secondary" size="sm" iconLeft={<Icons.Cog size={14} />} onClick={() => setIsEditMode(true)}>
              {t('dashboard:action.customize')}
            </Button>
          )
        }
      />

      {/* KPI Strip */}
      <KPIStrip>
        <KPI
          label={t('dashboard:kpi.revenue')}
          value={stats ? VND(stats.stats?.revenue?.current_month, { compact: true }) : '—'}
          delta={stats ? `+${pct(stats.stats?.revenue?.growth, { fromFraction: false })}` : undefined}
          deltaUp
          sub={t('dashboard:kpi.revenueSub')}
          icon={<CurrencyDollarIcon />}
          accent="primary"
        />
        <KPI
          label={t('dashboard:kpi.occupancy')}
          value={stats ? `${stats.stats?.occupancy?.occupied} / ${stats.stats?.occupancy?.total_rooms}` : '—'}
          sub={stats ? t('dashboard:kpi.occupancySub', { rate: stats.stats?.occupancy?.rate }) : undefined}
          icon={<HomeIcon />}
          accent="success"
        />
        <KPI
          label={t('dashboard:kpi.tickets')}
          value={stats ? String(stats.stats?.tickets?.open ?? '—') : '—'}
          sub={stats ? t('dashboard:kpi.ticketsSub', { count: stats.stats?.tickets?.in_progress ?? 0 }) : undefined}
          icon={<TicketIcon />}
          accent="warning"
        />
        <KPI
          label={t('dashboard:kpi.overdue')}
          value={stats ? String(stats.stats?.payments?.overdue ?? '—') : '—'}
          sub={stats ? t('dashboard:kpi.overdueSub', { count: stats.stats?.payments?.late ?? 0 }) : undefined}
          icon={<ExclamationTriangleIcon />}
          accent={stats?.stats?.payments?.overdue > 0 ? 'danger' : 'neutral'}
        />
      </KPIStrip>

      {/* Time Range Toolbar */}
      <div className="flex items-center justify-between bg-white rounded-2xl px-6 py-4 shadow-sm border border-stone-100">
        <div className="flex items-center gap-3">
          <CalendarDaysIcon size={20} className="text-teal-500" />
          <span className="text-sm font-medium text-stone-600">Data Period:</span>
          <span className="text-sm font-semibold text-teal-700">
            {timeRange.startMonth} to {timeRange.endMonth}
          </span>
        </div>
        <div className="relative" data-time-range-dropdown>
          <button
            onClick={() => setShowTimeRangeDropdown(!showTimeRangeDropdown)}
            className="px-4 py-2 bg-teal-50 hover:bg-teal-100 text-teal-700 rounded-xl transition-all flex items-center gap-2 font-medium border border-teal-200"
          >
            <span>{TIME_RANGE_PRESETS.find(p => p.id === timeRangePreset)?.label || '6 Months'}</span>
            <ChevronDownIcon size={16} className={`transition-transform ${showTimeRangeDropdown ? 'rotate-180' : ''}`} />
          </button>

          {showTimeRangeDropdown && (
            <div className="absolute right-0 top-full mt-2 w-64 bg-white rounded-xl shadow-2xl border border-stone-200 py-2 z-50 animate-fade-in max-h-[400px] overflow-y-auto">
              {TIME_RANGE_PRESETS.filter(p => p.id !== 'custom').map((preset) => (
                <button
                  key={preset.id}
                  onClick={() => {
                    setTimeRangePreset(preset.id);
                    setShowTimeRangeDropdown(false);
                  }}
                  className={`w-full px-4 py-2.5 text-left text-sm hover:bg-teal-50 transition-colors flex items-center justify-between ${
                    timeRangePreset === preset.id ? 'bg-teal-50 text-teal-700 font-semibold' : 'text-stone-700'
                  }`}
                >
                  <span>{preset.label}</span>
                  {timeRangePreset === preset.id && <CheckIcon size={16} className="text-teal-600" />}
                </button>
              ))}
              <div className="border-t border-stone-100 mt-2 pt-3 px-3">
                <p className="text-xs text-stone-500 mb-3 font-medium">Custom Range</p>
                <div className="space-y-2 mb-3">
                  <div>
                    <label className="text-[10px] text-stone-400 uppercase tracking-wide">From</label>
                    <input
                      type="month"
                      value={customRange?.startMonth || timeRange.startMonth}
                      onChange={(e) => {
                        const newStart = e.target.value;
                        setCustomRange(prev => ({
                          ...prev,
                          startMonth: newStart,
                          startDate: newStart + '-01'
                        }));
                        setTimeRangePreset('custom');
                      }}
                      className="w-full text-sm border border-stone-200 rounded-lg px-3 py-2 mt-1 focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                    />
                  </div>
                  <div>
                    <label className="text-[10px] text-stone-400 uppercase tracking-wide">To</label>
                    <input
                      type="month"
                      value={customRange?.endMonth || timeRange.endMonth}
                      onChange={(e) => {
                        const newEnd = e.target.value;
                        setCustomRange(prev => ({
                          ...prev,
                          endMonth: newEnd,
                          endDate: newEnd + '-28'
                        }));
                        setTimeRangePreset('custom');
                      }}
                      className="w-full text-sm border border-stone-200 rounded-lg px-3 py-2 mt-1 focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                    />
                  </div>
                </div>
                <button
                  onClick={() => setShowTimeRangeDropdown(false)}
                  className="w-full py-2.5 text-sm bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors font-medium"
                >
                  Apply
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Widget Grid */}
      {isEditMode ? (
        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId="dashboard-widgets">
            {(provided) => (
              <div
                {...provided.droppableProps}
                ref={provided.innerRef}
                className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4"
              >
                {widgets.map((widget, index) => (
                  <Draggable key={widget.id} draggableId={widget.id} index={index}>
                    {(provided, snapshot) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        className={`${getWidgetSizeClass(widget.size)} bg-white rounded-xl border-2 border-dashed border-teal-200 p-6 transition-all duration-200 shadow-sm ${snapshot.isDragging ? 'shadow-2xl scale-[1.03] ring-4 ring-teal-500/20 z-50 bg-white' : 'bg-white/50'
                          }`}
                      >
                        <div className="flex justify-between items-start mb-6">
                          <div className="flex items-center gap-3">
                            <div {...provided.dragHandleProps} className="p-2 rounded-lg bg-teal-50 hover:bg-teal-100 text-teal-400 hover:text-teal-600 transition-colors cursor-move">
                              <ArrowsPointingOutIcon size={20} />
                            </div>
                            <h3 className="font-bold text-stone-800 text-lg">{widget.name || widget.type}</h3>
                          </div>
                          <div className="flex gap-2">
                            <button
                              onClick={() => setEditingWidget(widget)}
                              className="p-2 rounded-lg hover:bg-stone-100 text-stone-400 hover:text-teal-600 transition-colors"
                            >
                              <PencilIcon size={20} />
                            </button>
                            <button
                              onClick={() => removeWidget(widget.id)}
                              className="p-2 rounded-lg hover:bg-red-50 text-stone-400 hover:text-red-600 transition-colors"
                            >
                              <TrashIcon size={20} />
                            </button>
                          </div>
                        </div>
                        <div className="opacity-70 pointer-events-none grayscale">
                          {renderWidget(widget)}
                        </div>
                      </div>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
          {widgets.map((widget, index) => (
            <div
              key={widget.id}
              className={`${getWidgetSizeClass(widget.size)} bg-white rounded-xl border border-stone-200 p-6 animate-fade-in hover:shadow-xl hover:shadow-teal-500/10 hover:-translate-y-1 transition-all duration-300 group`}
              style={{ animationDelay: `${index * 50}ms` }}
            >
              <div className="flex items-center gap-4 mb-6">
                <div className="w-12 h-12 rounded-2xl bg-teal-50 flex items-center justify-center group-hover:scale-110 group-hover:bg-teal-100 transition-all duration-300 shadow-sm">
                  {WIDGET_TEMPLATES.find(t => t.id === widget.type)?.icon && (
                    React.createElement(WIDGET_TEMPLATES.find(t => t.id === widget.type).icon, {
                      className: "w-6 h-6 text-teal-600"
                    })
                  )}
                </div>
                <h3 className="font-bold text-lg text-stone-800 tracking-tight">{widget.name || widget.type}</h3>
              </div>
              {renderWidget(widget)}
            </div>
          ))}
        </div>
      )}

      {/* Add Widget Modal */}
      {showAddWidget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-stone-900/60 backdrop-blur-sm animate-fade-in">
          <div
            className="bg-white rounded-3xl shadow-2xl max-w-3xl w-full max-h-[85vh] overflow-hidden flex flex-col animate-slide-up"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="px-8 py-6 border-b border-stone-100 flex items-center justify-between bg-white z-10">
              <div>
                <h2 className="text-2xl font-bold text-stone-800">Add Widget</h2>
                <p className="text-stone-500 mt-1">Choose a widget to add to your dashboard</p>
              </div>
              <Button kind="ghost" size="sm" onClick={() => setShowAddWidget(false)}>
                <XMarkIcon size={24} />
              </Button>
            </div>

            {/* Modal Body */}
            <div className="p-8 overflow-y-auto scrollbar-thin scrollbar-thumb-stone-200">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-5">
                {WIDGET_TEMPLATES.map((template, index) => (
                  <button
                    key={template.id}
                    onClick={() => addWidget(template)}
                    className="flex flex-col items-center p-6 border border-stone-200 rounded-2xl hover:border-teal-500 hover:bg-stone-50 hover:shadow-lg hover:shadow-teal-500/10 transition-all duration-300 group text-center animate-fade-in h-40 justify-center"
                    style={{ animationDelay: `${index * 50}ms` }}
                  >
                    <div className="w-14 h-14 rounded-2xl bg-teal-50 flex items-center justify-center mb-4 group-hover:bg-teal-600 group-hover:scale-110 transition-all duration-300 shadow-sm group-hover:shadow-teal-500/30">
                      <template.icon className="w-7 h-7 text-teal-600 group-hover:text-white transition-colors duration-300" />
                    </div>
                    <div>
                      <p className="font-bold text-stone-700 group-hover:text-teal-700 transition-colors">{template.name}</p>
                      <p className="text-[10px] text-stone-400 mt-1 uppercase tracking-wider font-semibold">{template.type}</p>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Modal Footer */}
            <div className="px-8 py-5 border-t border-stone-100 bg-stone-50 flex justify-end">
              <Button kind="secondary" onClick={() => setShowAddWidget(false)}>Cancel</Button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Widget Modal */}
      {editingWidget && (
        <WidgetSettingsModal
          widget={editingWidget}
          onSave={updateWidget}
          onClose={() => setEditingWidget(null)}
        />
      )}
    </div>
    </DashboardTimeRangeContext.Provider>
  );
};

export default DashboardEnhanced;
